import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/SetupIntentsService';
import {SetupIntent} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const useSetupIntent = (setupIntentId?: string) => {
    const queryClient = useQueryClient();

    const setupIntentQuery = useQuery({
        queryKey: ['setupIntent', setupIntentId],
        queryFn: () => fetch(setupIntentId!),
        enabled: !!setupIntentId,
    });

    const createMutation = useMutation({
        mutationFn: (values: SetupIntent) => {
            const payload = {...values} as Record<string, unknown>;
            delete payload.created;
            return add(payload as SetupIntent);
        },
        onSuccess: () => {
            notifySuccess('Setup intent created');
            queryClient.invalidateQueries({queryKey: ['setupIntents']});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: SetupIntent) => {
            if (!setupIntentId) throw new Error('setupIntentId is required parameter');
            return update(setupIntentId, values);
        },
        onSuccess: () => {
            notifySuccess('Setup intent updated');
            queryClient.invalidateQueries({queryKey: ['setupIntents']});
            queryClient.invalidateQueries({queryKey: ['setupIntent', setupIntentId]});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!setupIntentId) throw new Error('setupIntentId is required parameter');
            return deleteRecord(setupIntentId);
        },
        onSuccess: () => {
            notifySuccess('Setup intent deleted');
            queryClient.invalidateQueries({queryKey: ['setupIntents']});
        },
        onError: notifyError,
    });

    return {
        record: setupIntentQuery.data,
        loading: setupIntentQuery.isLoading,
        fetchRecord: setupIntentQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
